import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronDown, ChevronUp, Shield, Smartphone, Upload, Users, DollarSign, Clock } from 'lucide-react';

const CreatorFAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      icon: Shield,
      question: "How private are my family videos?",
      answer: "Completely private! Your videos are only visible to people you specifically invite. We don't have ads, algorithms, or public discovery. It's like having a private family photo album that only your loved ones can see. You control exactly who sees what, and you can change permissions anytime."
    },
    {
      icon: Smartphone,
      question: "Can I upload videos directly from my phone?",
      answer: "Absolutely! HiveShare Creator is designed mobile-first. You can upload videos directly from your iPhone's camera or photo library with just a few taps. No need to transfer files to a computer or deal with complicated processes. Capture a moment and share it instantly."
    },
    {
      icon: Upload,
      question: "Is there a limit on how many videos I can upload?",
      answer: "Nope! Upload as many videos as you want. Whether it's every soccer game, piano lesson, or daily family moment - there's no artificial limit. We believe your memories shouldn't be constrained by storage quotas."
    },
    {
      icon: Users,
      question: "How many people can I share with?",
      answer: "You can invite as many family members and friends as you want! Grandparents, aunts, uncles, close family friends - everyone who matters can stay connected to your family's journey. Each person gets their own secure access to the videos you choose to share with them."
    },
    {
      icon: DollarSign,
      question: "Why $9/month instead of free like other platforms?",
      answer: "Because your family deserves better than ads, data mining, and strangers seeing your kids. Free platforms make money by selling your data and showing ads. We make money from your subscription, which means we work for YOU, not advertisers. Plus, $9 is less than two coffee shop visits!"
    },
    {
      icon: Clock,
      question: "What if I want to cancel? Do I lose my videos?",
      answer: "You can cancel anytime, and you'll never lose your memories! Before canceling, you can download all your videos to keep forever. We also keep your videos accessible for 30 days after canceling, just in case you change your mind. No hostage situations with your precious memories."
    }
  ];

  const additionalQuestions = [
    {
      question: "Can grandparents who aren't tech-savvy use this?",
      answer: "Yes! We designed HiveShare Creator to be incredibly simple. Grandparents just need to click a link in their email, and they can watch videos immediately. The interface is clean and straightforward - no confusing buttons or complicated menus."
    },
    {
      question: "What about my kids' privacy and safety?",
      answer: "Your kids' safety is our top priority. Videos are private by default, there's no public discovery, no commenting from strangers, and no advertising. It's designed specifically for families, not for social media engagement. You control exactly who sees your children."
    },
    {
      question: "Can I organize videos by child or activity?",
      answer: "Absolutely! Videos automatically organize by date, and you can add tags for different children, activities, or events. Looking for 'Emma's soccer games' or 'piano recitals'? You'll find them instantly with our smart organization features."
    },
    {
      question: "What if I'm teaching lessons to other people's kids?",
      answer: "Perfect use case! You can create separate video libraries for each family you work with. Parents only see videos of their own children, maintaining privacy while letting you share progress and technique videos with each family individually."
    },
    {
      question: "Do I need to install anything?",
      answer: "Nope! HiveShare Creator works in your web browser and has a simple iOS app for easy mobile uploads. Viewers don't need to install anything - they just click a link and watch. It's designed to be as friction-free as possible for busy families."
    },
    {
      question: "What if I want to upgrade later?",
      answer: "If you're growing into coaching multiple families or running a small program, you can easily upgrade to HiveShare Studio ($29/month) for organization management features. All your videos and family connections transfer seamlessly - no data loss, no starting over."
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Questions? We've Got Answers
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to know about keeping your family's video memories safe, organized, and accessible.
          </p>
        </motion.div>

        {/* Main FAQ Items */}
        <div className="max-w-4xl mx-auto mb-16">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="mb-4"
            >
              <Card className="overflow-hidden border-2 border-gray-200 hover:border-purple-200 transition-colors duration-300">
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full text-left"
                >
                  <CardContent className="p-6 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200">
                    <div className="flex items-center">
                      <div className="p-2 rounded-full bg-purple-100 mr-4">
                        <faq.icon className="h-5 w-5 text-purple-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                    </div>
                    {openIndex === index ? (
                      <ChevronUp className="h-5 w-5 text-purple-600" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </CardContent>
                </button>
                
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 pt-0 border-t border-gray-100">
                        <p className="text-gray-700 leading-relaxed pl-14">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Additional Questions */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            More Questions? More Answers!
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            {additionalQuestions.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full border border-gray-200 hover:shadow-md transition-shadow duration-300">
                  <CardContent className="p-6">
                    <h4 className="font-semibold text-gray-900 mb-3">{item.question}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.answer}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Contact Support */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-16"
        >
          <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 max-w-2xl mx-auto">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Still Have Questions?
              </h3>
              <p className="text-gray-600 mb-6">
                We're here to help! Our friendly support team responds to every question personally.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300">
                  Chat with Support
                </button>
                <button className="border-2 border-purple-300 text-purple-700 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50 transition-all duration-300">
                  Email Us
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                📞 Real humans, not bots • ⚡ Usually respond within 2 hours
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default CreatorFAQ;